/**
 * F23 (Wave 2C) — admin "void statement" modal.
 *
 * Composes Modal + FormField + Textarea (reason 1–500) + destructive warning
 * Banner + a type-to-confirm field (user types the venue name to enable the
 * destructive button). The "type-to-confirm" pattern mirrors F11's destructive
 * Suspend/Reassign dialogs.
 *
 * NOTE: voiding an `issued` statement detaches its records (they roll into
 * the next period); voiding a `settled` statement does NOT detach (incident
 * response only). The Banner copy explains this so the admin sees the
 * implication before they type.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Textarea, TextInput } from '@bravemove/ui-web';
import { VoidStatementInputSchema, type VoidStatementInput } from '@bravemove/types';

export interface VoidStatementDialogProps {
  readonly open: boolean;
  readonly statementId: string;
  readonly venueName: string;
  readonly currentStatus: 'issued' | 'settled';
  readonly onConfirm: (input: VoidStatementInput) => Promise<void>;
  readonly onClose: () => void;
}

export function VoidStatementDialog({
  open,
  statementId,
  venueName,
  currentStatus,
  onConfirm,
  onClose,
}: VoidStatementDialogProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setReason('');
    setConfirmName('');
    setSubmitting(false);
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const validation = useMemo(
    () => VoidStatementInputSchema.safeParse({ reason: reason.trim() }),
    [reason],
  );

  const nameMatches = confirmName.trim() === venueName.trim() && venueName.trim().length > 0;

  const disableSubmit = submitting || !validation.success || !nameMatches;

  const submit = async () => {
    if (!validation.success || !nameMatches) return;
    setSubmitting(true);
    try {
      await onConfirm(validation.data);
      onClose();
    } catch {
      // CST-008 — the page's onConfirm surfaces the failure via a danger toast
      // and re-throws; swallow here so it never escapes `void submit()` as an
      // unhandled rejection and the dialog stays OPEN for a retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:commission.dialog.void.title', { defaultValue: 'Void statement' })}
      description={t('admin:commission.dialog.void.description', {
        defaultValue:
          'Voiding an issued statement detaches its records so they roll into the next period. Voiding a settled statement does NOT detach — use only for incident response.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="void-statement-cancel"
          >
            {t('admin:commission.dialog.void.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="void-statement-confirm"
          >
            {t('admin:commission.dialog.void.confirm', { defaultValue: 'Void statement' })}
          </Button>
        </>
      }
    >
      <div
        className="flex flex-col gap-4"
        data-testid="void-statement-dialog"
        data-statement-id={statementId}
        data-current-status={currentStatus}
      >
        <div data-testid="void-statement-warning">
          <Banner tone="warning">
            {t('admin:commission.dialog.void.warning_banner', {
              defaultValue:
                'This action is permanent. Records may or may not detach depending on the prior status.',
            })}
          </Banner>
        </div>

        <FormField
          label={t('admin:commission.dialog.void.reason_label', {
            defaultValue: 'Reason (audit-logged)',
          })}
          required
        >
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={500}
            data-testid="void-statement-reason"
            aria-label={t('admin:commission.dialog.void.reason_label', {
              defaultValue: 'Reason (audit-logged)',
            })}
          />
        </FormField>

        <FormField
          label={t('admin:commission.dialog.void.type_to_confirm_label', {
            defaultValue: 'Type the venue name to confirm',
          })}
          helper={t('admin:commission.dialog.void.type_to_confirm_helper', {
            defaultValue: 'Type the venue name exactly to enable the Void button.',
          })}
          required
        >
          <TextInput
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={venueName}
            data-testid="void-statement-confirm-name"
            aria-label={t('admin:commission.dialog.void.type_to_confirm_label', {
              defaultValue: 'Type the venue name to confirm',
            })}
          />
        </FormField>
      </div>
    </Modal>
  );
}

export default VoidStatementDialog;
